import React, { useState, useEffect } from "react";
import { Button, Input, FormControl } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CameraAlt } from "@mui/icons-material";
import {
    ErrorTypography,
    ImageDescription,
    ImageTitleTypography,
    ImageTypography,
    LogoNameTypography,
    SupportFormatTypography,
} from "../../elements/Typography/TypographyVariant";
import {
    ImageMainWrapper,
    StyledCloseIcon,
    StyledHelpIcon,
    SupportedFormatWrapper,
    ImageUploadedNameWrapper,
} from "../../../CompanyForm.styled";
import GlobalTheme from "../../../styles/global.theme";
import { LightTooltip, CustomIconButton } from "../../../MUIStyled.js";
import {
    requestPresignedURL,
    uploadToS3,
    getAssets,
    postAssets,
} from "../../../config/fetchAPI";
import Loading from "../../elements/Loading/Loading.js";
import uploadComplete from "../../../images/upload-complete.svg";

export default function DynamicLogoUpload({
    titleText,
    description,
    supportedFormatsText,
    buttonText,
    imageUploadError,
    type,
    onImageUploaded,
    setIsHeaderUploaded,
}) {
    const [logoName, setLogoName] = useState("");
    const [logoURL, setLogoURL] = useState("");
    const [fileType, setFileType] = useState("");
    const [fetchedAssets, setFetchedAssets] = useState(null);
    const [isLoadingAssets, setIsLoadingAssets] = useState(true);
    const [isLoadingS3, setIsLoadingS3] = useState(false);

    // Load existing assets
    useEffect(() => {
        const loadAssets = async () => {
            try {
                const assets = await getAssets();
                setFetchedAssets(assets);
            } catch (error) {
                console.error("Error loading assets:", error);
                setFetchedAssets([]);
            } finally {
                setIsLoadingAssets(false);
            }
        };

        loadAssets();
    }, []);

    // Filter and display asset by type
    useEffect(() => {
        if (Array.isArray(fetchedAssets) && fetchedAssets.length > 0) {
            // Find the asset that matches the current type
            const matchingAsset = fetchedAssets.find(
                (asset) => asset.type === type
            );

            if (matchingAsset) {
                setLogoName(matchingAsset.filename);
                setLogoURL(matchingAsset.path);
                // Detect file type from the path/filename
                const extension = matchingAsset.filename
                    .split(".")
                    .pop()
                    .toLowerCase();
                if (extension === "pdf") {
                    setFileType("application/pdf");
                } else {
                    setFileType(`image/${extension}`);
                }
            }
        }
    }, [fetchedAssets, type]);

    // passing logoURL to parent
    useEffect(() => {
        if (logoURL) {
            onImageUploaded(logoURL, isLoadingS3);
        }
    }, [logoURL, onImageUploaded, isLoadingS3]);

    const handleTooltipText = () => {
        if (type === "logo") {
            return (
                <>
                    The Steer Portal is designed to sit on the staff benefits
                    page on your intranet, where your employees start their
                    novated lease journey and can book in a Consultation with
                    our expert Leasing Consultants at a time that suits them.
                    <br />
                    <br />
                    Your employees can access this via their work email domain.
                    <br />
                    <br />
                    Please feel free to test drive Steer for yourself, which we
                    are able to co-brand with you to facilitate a personalised
                    experience for your employees.
                </>
            );
        } else if (type === "header") {
            return (
                <>
                    The request to upload a snippet of your bank statement is to
                    help us verify your details and ensure we are debiting or
                    refunding the correct account.
                    <br />
                    <br />
                    This does not need to be a full bank statement and can be
                    either a snippet of your statement or a deposit slip from
                    your bank.
                </>
            );
        }
    };

    // call RequestPresignedURL to allow image upload
    const requestLogoURL = async (imageName, imageType, type) => {
        try {
            let result = await requestPresignedURL(imageName, imageType, type);
            return result;
        } catch (error) {
            return error;
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];

        if (file) {
            setFileType(file.type);
            setLogoName(file.name);

            try {
                // Request presigned URL
                const presignedData = await requestLogoURL(
                    file.name,
                    file.type,
                    type
                );

                if (presignedData && presignedData.uploadUrl) {
                    // Upload file to S3
                    const uploadResult = await uploadToS3(presignedData, file);
                    setIsLoadingS3(true);

                    if (uploadResult.success) {
                        setIsLoadingS3(false);
                        // postAsset
                        await postAssets(file.name, presignedData.path, type);

                        // Reload assets to ensure we have the latest data
                        const updatedAssets = await getAssets();
                        setFetchedAssets(updatedAssets);

                        // Show preview using the S3 path instead of local file
                        setLogoURL(presignedData.path);
                    }
                }
            } catch (error) {
                console.error("Error uploading file:", error);
                return error;
            }
        }
    };

    // Handle logo removal
    const handleRemoveLogo = () => {
        setLogoName("");
        setLogoURL("");
        if (type === "header") {
            setIsHeaderUploaded(false);
        }
        document.getElementById("logo-upload").value = null;
    };

    return (
        <ImageMainWrapper>
            <Grid
                container
                spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <ImageTypography>{titleText}</ImageTypography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <ImageDescription>
                        {description}
                        <LightTooltip title={handleTooltipText()}>
                            <CustomIconButton size="small">
                                <StyledHelpIcon />
                            </CustomIconButton>
                        </LightTooltip>
                    </ImageDescription>
                </Grid>

                <SupportedFormatWrapper>
                    <Grid size={{ xs: 12 }}>
                        <SupportFormatTypography>
                            {supportedFormatsText}
                        </SupportFormatTypography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <FormControl>
                            <Input
                                id="logo-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                sx={{ display: "none" }}
                            />
                        </FormControl>
                    </Grid>

                    <ImageUploadedNameWrapper>
                        {isLoadingAssets && isLoadingS3 ? (
                            <Loading />
                        ) : (
                            <>
                                <Grid>
                                    {logoURL ? (
                                        type === "header" ? (
                                            <img
                                                src={uploadComplete}
                                                alt="Header uploaded"
                                                style={{
                                                    width: "auto",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    height: "40px",
                                                    objectFit: "contain",
                                                }}
                                            />
                                        ) : fileType === "application/pdf" ? (
                                            <iframe
                                                src={logoURL}
                                                title="Uploaded PDF"
                                                style={{
                                                    width: "100%",
                                                    height: "150px",
                                                    border: "none",
                                                }}
                                            />
                                        ) : (
                                            <img
                                                src={logoURL}
                                                alt="Uploaded company logo"
                                                style={{
                                                    width: "auto",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    height: "80px",
                                                    objectFit: "contain",
                                                    overflow: "hidden",
                                                }}
                                            />
                                        )
                                    ) : (
                                        <Button
                                            component="span"
                                            sx={{
                                                width: "100%",
                                                padding: "30px",
                                                backgroundColor:
                                                    GlobalTheme.palette.grey
                                                        .primary.main,
                                                color: GlobalTheme.palette.black
                                                    .primary.main,
                                                textAlign: "center",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                            startIcon={
                                                <CameraAlt
                                                    sx={{
                                                        color: `${GlobalTheme.palette.black.primary.main}`,
                                                    }}
                                                />
                                            }
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        "logo-upload"
                                                    )
                                                    .click()
                                            }>
                                            <ImageTitleTypography>
                                                {buttonText}
                                            </ImageTitleTypography>
                                        </Button>
                                    )}
                                </Grid>
                                {logoName && (
                                    <Grid>
                                        <LogoNameTypography>
                                            {logoName}
                                            <StyledCloseIcon
                                                onClick={handleRemoveLogo}
                                            />
                                        </LogoNameTypography>
                                    </Grid>
                                )}
                            </>
                        )}
                    </ImageUploadedNameWrapper>

                    {/* Display the error message */}
                    {imageUploadError && (
                        <Grid size={{ xs: 12 }}>
                            <ErrorTypography
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    textAlign: "left",
                                }}>
                                {imageUploadError}
                            </ErrorTypography>
                        </Grid>
                    )}
                </SupportedFormatWrapper>
            </Grid>
        </ImageMainWrapper>
    );
}
